import React, { useState, useCallback, useMemo } from 'react';
import { Button, Calendar, CardComposer, Row, SelectOptionsClose, SelectOptionsCloseLabel } from '@app/components';
import { modalsSelectors, packApi, productApi } from '@app/store';
import { SvgSwitch } from '@app/components/icons/SvgSwitch';
import { SvgTransfer } from '@app/components/icons/SvgTransfer';
import { SlantTitle } from '@app/components/typography';
import { theme } from '@app/theme';
import { extractData } from '@app/helpers/utils';
import { Modal } from '@app/components/modals';
import { useAppSelector } from '@app/store/hooks';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useModal } from '@app/hooks/useModal';
import { CatalogToCatalogProduct } from './CatalogToCatalogProduct';
import { ICatalogToProduct } from '@app/common/src/interfaces/catalog/catalog-to-product.interface';
import { ICatalogToTag } from '@app/common/src/interfaces/catalog/catalog-to-tag.interface';
import { ICatalogToPack, IPack, IProduct, ITag } from '@app/common';
import { CatalogToCatalogPack } from './CatalogToCatalogPack';
import { normalize } from '@app/helpers';
import styled from '@2pulse/responsive';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { DndContext } from '@dnd-kit/core';
import { Separator } from '@app/components/styled/Separator';

const modifiers = [restrictToVerticalAxis];

export const CatalogOverrides = ({ modalId }: { modalId: string }) => {
  const modal = useModal(modalId);
  const { control, watch } = useFormContext();

  const [selectedTagId, setSelectedTagId] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const modalState = useAppSelector(state => modalsSelectors.selectById(state, modalId));

  const [
    name,
    catalogToTags = []
  ] = watch(['name', 'catalogToTags']);

  const tags = useMemo(() =>
    (catalogToTags as ICatalogToTag[]).map(catalogToTag => catalogToTag.tag),
    [catalogToTags]
  );

  const { remove: removeProduct, fields: productFields, move: moveProduct, insert: insertProduct } = useFieldArray({
    name: 'catalogToProducts',
    control
  });

  const { remove: removePack, fields: packsFields, move: movePack, insert: insertPack } = useFieldArray({
    name: 'catalogToPacks',
    control
  });

  const packIdsInCatalog = useMemo(() =>
    packsFields.map(catalogToPack => (catalogToPack as unknown as ICatalogToPack)?.pack?._id),
    [packsFields]
  );

  const productIdsInCatalog = useMemo(() =>
    productFields.map(catalogToProduct => (catalogToProduct as unknown as ICatalogToProduct)?.product?._id),
    [productFields]
  );

  const { data: productsData } = productApi.useReadManyProductsQuery({
    filters: {},
    sort: ['name:ASC'],
    populate: [
      'tags',
      'productVariations'
    ],
  });

  const { data: packsData } = packApi.useReadManyPacksQuery({
    filters: {},
    sort: ['name:ASC'],
    populate: [
      'tags',
    ],
  });

  const products = extractData(productsData)
    .filter(product => {
      const isInCatalog = productIdsInCatalog.some(productId => productId === product._id);
      const hasSelectedTag = !selectedTagId || product.tagsIds.includes(selectedTagId);
      const matchesSearch = !searchTerm || product.name.toLowerCase().includes(searchTerm.toLowerCase());

      return !isInCatalog && hasSelectedTag && matchesSearch;
    });

  const packs = extractData(packsData)
    .filter(pack => {
      const isInCatalog = packIdsInCatalog.some(packId => packId === pack._id);
      const hasSelectedTag = !selectedTagId || pack.tagsIds.includes(selectedTagId);
      const matchesSearch = !searchTerm || pack.name.toLowerCase().includes(searchTerm.toLowerCase());

      return !isInCatalog && hasSelectedTag && matchesSearch;
    });

  // Filtrage des fields avec vérification d'existence des propriétés
  const filteredPacksFields = useMemo(() => {
    return packsFields.filter((field: any) => {
      if (!field?.pack) return false;
      const hasSelectedTag = !selectedTagId || (field.pack.tagsIds && field.pack.tagsIds.includes(selectedTagId));
      const matchesSearch = !searchTerm || (field.pack.name && field.pack.name.toLowerCase().includes(searchTerm.toLowerCase()));
      return hasSelectedTag && matchesSearch;
    });
  }, [packsFields, selectedTagId, searchTerm]);

  const filteredProductFields = useMemo(() => {
    return productFields.filter((field: any) => {
      if (!field?.product) return false;
      const hasSelectedTag = !selectedTagId || (field.product.tagsIds && field.product.tagsIds.includes(selectedTagId));
      const matchesSearch = !searchTerm || (field.product.name && field.product.name.toLowerCase().includes(searchTerm.toLowerCase()));
      return hasSelectedTag && matchesSearch;
    });
  }, [productFields, selectedTagId, searchTerm]);

  // Créer des IDs uniques pour SortableContext basés sur les fields filtrés
  const packSortableIds = useMemo(() =>
    filteredPacksFields.map(field => field.id),
    [filteredPacksFields]
  );

  const productSortableIds = useMemo(() =>
    filteredProductFields.map(field => field.id),
    [filteredProductFields]
  );

  const onSelectTag = useCallback((tag?: ITag) => {
    setSelectedTagId(tag?._id);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const onPackDragEnd = useCallback((event: any) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Trouver les indices dans le tableau complet (non filtré)
      const activeField = packsFields.find(field => field.id === active.id);
      const overField = packsFields.find(field => field.id === over.id);

      if (activeField && overField) {
        const activeIndex = packsFields.findIndex(field => field.id === active.id);
        const overIndex = packsFields.findIndex(field => field.id === over.id);

        if (activeIndex !== -1 && overIndex !== -1) {
          movePack(activeIndex, overIndex);
        }
      }
    }
  }, [movePack, packsFields]);

  const onProductDragEnd = useCallback((event: any) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Trouver les indices dans le tableau complet (non filtré)
      const activeField = productFields.find(field => field.id === active.id);
      const overField = productFields.find(field => field.id === over.id);

      if (activeField && overField) {
        const activeIndex = productFields.findIndex(field => field.id === active.id);
        const overIndex = productFields.findIndex(field => field.id === over.id);

        if (activeIndex !== -1 && overIndex !== -1) {
          moveProduct(activeIndex, overIndex);
        }
      }
    }
  }, [moveProduct, productFields]);

  const addProduct = (product: IProduct) => {
    const insertIndex = productFields.findIndex((field: any) =>
      field.product.name.localeCompare(product.name) > 0
    );

    const finalIndex = insertIndex === -1 ? productFields.length : insertIndex;

    insertProduct(finalIndex, {
      product,
      catalogProductToProductVariations: product.productVariations.map(productVariation => ({
        price: null,
        deliveryPrice: null,
        takeAwayPrice: null,
        onSpotPrice: null,
        disabled: false,
        productVariation
      }))
    });
  }

  const addPack = (pack: IPack) => {
    const insertIndex = packsFields.findIndex((field: any) =>
      field.pack.name.localeCompare(pack.name) > 0
    );

    const finalIndex = insertIndex === -1 ? packsFields.length : insertIndex;

    insertPack(finalIndex, {
      pack,
      disabled: false
    });
  }

  const handleRemovePack = useCallback((fieldId: string) => {
    const realIndex = packsFields.findIndex(field => field.id === fieldId);
    if (realIndex !== -1) {
      removePack(realIndex);
    }
  }, [removePack, packsFields]);

  const handleRemoveProduct = useCallback((fieldId: string) => {
    const realIndex = productFields.findIndex(field => field.id === fieldId);
    if (realIndex !== -1) {
      removeProduct(realIndex);
    }
  }, [removeProduct, productFields]);

  return (
    <Modal isOpen={!!modalState?.isOpen}>
      <Calendar.Details>

        <Calendar.DetailsHeader>
          <Calendar.DetailsTitle numberOfLines={2}>{name}</Calendar.DetailsTitle>

          <SelectOptionsClose>
            <SelectOptionsCloseLabel>
              <Button
                title="Fermer"
                grow
                large
                bordered
                onPress={() => modal.close()} />
            </SelectOptionsCloseLabel>
          </SelectOptionsClose>
        </Calendar.DetailsHeader>

        <Calendar.DetailsBody>

          <CardComposer>

            <CardComposer.Container>

              <CardComposer.ProductList>

                <SlantTitle label="Vos produits & menus" color={theme.colors.gold} />

                <CardComposer.List contentContainerStyle={{ display: 'flex', rowGap: 13 }} showsVerticalScrollIndicator={false}>

                  <Separator value="Menus" />

                  {packs.length > 0 ? (
                    packs.map((pack) => (
                      <CardComposer.ListItem key={pack._id} onPress={() => addPack(pack)}>
                        <CardComposer.ListItemName>{pack.name}</CardComposer.ListItemName>
                        <Row align='center'>
                          <CardComposer.ListItemCategory>{pack.tags[0]?.name}</CardComposer.ListItemCategory>
                          <CardComposer.ListItemIcon>
                            <SvgTransfer color={theme.colors.babypowder} />
                          </CardComposer.ListItemIcon>
                        </Row>
                      </CardComposer.ListItem>
                    ))
                  ) : (
                    <EmptyMessage>Aucun menu</EmptyMessage>
                  )}

                  <Separator value="Produits" />

                  {products.length > 0 ? (
                    products.map((product) => (
                      <CardComposer.ListItem key={product._id} onPress={() => addProduct(product)}>
                        <CardComposer.ListItemName>{product.name}</CardComposer.ListItemName>
                        <Row align='center'>
                          <CardComposer.ListItemCategory>{product.tags[0]?.name}</CardComposer.ListItemCategory>
                          <CardComposer.ListItemIcon>
                            <SvgTransfer color={theme.colors.babypowder} />
                          </CardComposer.ListItemIcon>
                        </Row>
                      </CardComposer.ListItem>
                    ))
                  ) : (
                    <EmptyMessage>Aucun produit</EmptyMessage>
                  )}

                </CardComposer.List>

              </CardComposer.ProductList>

              <CardComposer.Divider>

                <CardComposer.DividerHeader>
                  <SvgSwitch color={theme.colors.babypowder} />
                </CardComposer.DividerHeader>

                <SearchContainer>
                  <SearchInputContainer>
                    <SearchInput
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChangeText={setSearchTerm}
                      autoCapitalize="none"
                      placeholderTextColor={theme.colors.babypowder}
                    />
                    {searchTerm.length > 0 && (
                      <ClearButton onPress={clearSearch}>
                        <ClearIcon>×</ClearIcon>
                      </ClearButton>
                    )}
                  </SearchInputContainer>
                </SearchContainer>

                <CardComposer.Categories contentContainerStyle={{ gap: 8 }}>
                  <Button
                    title="Toutes"
                    onPress={() => onSelectTag()}
                    square
                    bordered={!!selectedTagId} />

                  {tags.map(tag => (
                    <Button
                      key={tag._id}
                      onPress={() => onSelectTag(tag)}
                      title={tag.name}
                      square
                      bordered={selectedTagId !== tag._id} />
                  ))}
                </CardComposer.Categories>

              </CardComposer.Divider>

              <CardComposer.ProductList>

                <SlantTitle label="Votre catalogue" color={theme.colors.gold} />

                <CardComposer.List contentContainerStyle={{ display: 'flex', rowGap: 13 }} showsVerticalScrollIndicator={false}>

                  <Separator value="Menus" />

                  {filteredPacksFields.length > 0 ? (
                    <DndContext
                      modifiers={modifiers}
                      onDragEnd={onPackDragEnd}>
                      <SortableContext items={packSortableIds} strategy={verticalListSortingStrategy}>
                        {filteredPacksFields.map((field: any) => {
                          const index = packsFields.findIndex(f => f.id === field.id);
                          return (
                            <CatalogToCatalogPack
                              key={field.id}
                              id={field.id}
                              name={`catalogToPacks.${index}`}
                              onRemove={() => handleRemovePack(field.id)} />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <EmptyMessage>Aucun menu</EmptyMessage>
                  )}

                  <Separator value="Produits" />

                  {filteredProductFields.length > 0 ? (
                    <DndContext
                      modifiers={modifiers}
                      onDragEnd={onProductDragEnd}>
                      <SortableContext items={productSortableIds} strategy={verticalListSortingStrategy}>
                        {filteredProductFields.map((field: any) => {
                          const index = productFields.findIndex(f => f.id === field.id);
                          return (
                            <CatalogToCatalogProduct
                              key={field.id}
                              id={field.id}
                              name={`catalogToProducts.${index}`}
                              catalogToProduct={field}
                              onRemove={() => handleRemoveProduct(field.id)} />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <EmptyMessage>Aucun produit</EmptyMessage>
                  )}

                </CardComposer.List>

              </CardComposer.ProductList>

            </CardComposer.Container>

          </CardComposer>

        </Calendar.DetailsBody>

      </Calendar.Details>
    </Modal>
  )

}

const EmptyMessage = styled.Text(({ theme }) => ({
  color: theme.colors.babypowder,
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  textAlign: 'center',
  marginVertical: normalize(20),
}));

const SearchContainer = styled.View({
  alignItems: 'center',
  paddingHorizontal: normalize(20),
  paddingVertical: normalize(15),
});

const SearchInputContainer = styled.View({
  position: 'relative',
  width: '100%',
  maxWidth: 400,
});

const SearchInput = styled.TextInput(({ theme }) => ({
  width: '100%',
  backgroundColor: 'rgba(240, 249 , 252, .04)',
  borderRadius: normalize(8),
  color: theme.colors.babypowder,
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  paddingHorizontal: normalize(15),
  paddingVertical: normalize(12),
  paddingRight: normalize(40),
  '::placeholder': {
    color: theme.colors.babypowder,
    opacity: 0.7,
  },
}));

const ClearButton = styled.TouchableOpacity({
  position: 'absolute',
  right: normalize(12),
  top: '50%',
  transform: [{ translateY: -normalize(10) }],
  width: normalize(20),
  height: normalize(20),
  alignItems: 'center',
  justifyContent: 'center',
});

const ClearIcon = styled.Text(({ theme }) => ({
  color: theme.colors.babypowder,
  fontSize: normalize(16),
  fontWeight: 'bold',
  opacity: 0.7,
}));